import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { SAP_PUSH_STEP_LABELS, type SapStepStatus, type SapPushStep } from '@hcc/types';
import { ModalShell } from '../../../../components/ui/ModalShell';
import { Pill } from '../../../../components/ui/Pill';
import { Button } from '../../../../components/ui/Button';

export interface DryRunStep {
  key: SapPushStep;
  status: SapStepStatus;
  note?: string;
  error?: string;
  rendered_payload?: string;
}

const TONE: Record<SapStepStatus, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  pending: 'neutral',
  running: 'info',
  succeeded: 'success',
  warning: 'warning',
  failed: 'danger',
  skipped: 'neutral',
};

/**
 * Dry-run result: the exact payloads a live push would send, step by
 * step, plus the outcome of the read-only checks (bank lookup). What is
 * shown here is byte-identical to the live push payloads.
 */
export function SapDryRunModal({
  steps,
  onClose,
}: {
  steps: DryRunStep[];
  onClose: () => void;
}) {
  return (
    <ModalShell open onClose={onClose} titleId="sap-dryrun-title" maxWidth="max-w-3xl">
      <div className="p-5">
        <h2 id="sap-dryrun-title" className="text-base font-semibold">
          Dry run result
        </h2>
        <p className="mt-1 text-sm text-fg-muted">
          Nothing was written to SAP. The payloads below are exactly what the real push will send;
          the bank lookup ran live (read-only).
        </p>
        <div className="mt-4 space-y-2">
          {steps.map((s) => (
            <DryRunStepRow key={s.key} step={s} />
          ))}
        </div>
        <div className="mt-5 flex justify-end">
          <Button variant="secondary" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

function DryRunStepRow({ step }: { step: DryRunStep }) {
  const [open, setOpen] = useState(false);
  const hasPayload = !!step.rendered_payload;
  return (
    <div className="rounded-token-sm border border-line/10">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
        onClick={() => hasPayload && setOpen((o) => !o)}
      >
        {hasPayload ? (
          open ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-fg-dim" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-fg-dim" />
          )
        ) : (
          <span className="w-4" />
        )}
        <span className="font-medium">{SAP_PUSH_STEP_LABELS[step.key]}</span>
        <Pill tone={TONE[step.status]}>{step.status}</Pill>
        {step.note ? <span className="text-xs text-fg-muted">{step.note}</span> : null}
        {step.error ? <span className="text-xs text-danger-soft-fg">{step.error}</span> : null}
      </button>
      {open && hasPayload ? (
        <pre className="max-h-56 overflow-auto border-t border-line/10 bg-surface-inset p-3 text-xs leading-relaxed">
          {step.rendered_payload}
        </pre>
      ) : null}
    </div>
  );
}
